/**
 * Task page wrapper:
 * - Admins: full scaffolder UI for any task (and only admins use Ticket logs / lookup by request #).
 * - GitLab Developer+ on the template repo: full UI only for runs linked to their own Firestore ticket;
 *   another user’s task → “not your ticket”.
 * - Others: restricted UI for their runs; “not your ticket” when the task id belongs to someone else.
 */

import { useCallback, useEffect, useState, type ComponentType, type ReactNode } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import Box from '@material-ui/core/Box';
import Button from '@material-ui/core/Button';
import CircularProgress from '@material-ui/core/CircularProgress';
import IconButton from '@material-ui/core/IconButton';
import Paper from '@material-ui/core/Paper';
import Tooltip from '@material-ui/core/Tooltip';
import Typography from '@material-ui/core/Typography';
import { makeStyles } from '@material-ui/core/styles';
import CheckCircle from '@material-ui/icons/CheckCircle';
import FileCopy from '@material-ui/icons/FileCopy';
import { stringifyEntityRef } from '@backstage/catalog-model';
import { Content, ErrorPanel, Header, Page, Progress } from '@backstage/core-components';
import {
  AnalyticsContext,
  useAnalytics,
  useApi,
  useRouteRef,
  configApiRef,
  fetchApiRef,
  identityApiRef,
} from '@backstage/core-plugin-api';
import { useTranslationRef } from '@backstage/core-plugin-api/alpha';
import { entityPresentationApiRef } from '@backstage/plugin-catalog-react';
import { usePermission } from '@backstage/plugin-permission-react';
import { TaskPage as DefaultTaskPage, scaffolderPlugin } from '@backstage/plugin-scaffolder';
import {
  scaffolderApiRef,
  useTaskEventStream,
} from '@backstage/plugin-scaffolder-react';
import {
  taskCancelPermission,
  taskCreatePermission,
  taskReadPermission,
} from '@backstage/plugin-scaffolder-common/alpha';
import type { ScaffolderTaskOutput } from '@backstage/plugin-scaffolder-common';
import { useAsync as useAsync$1 } from '@react-hookz/web';
import { useAsync } from 'react-use';
import qs from 'qs';
import { scaffolderTranslationRef } from '@backstage/plugin-scaffolder/alpha';
import { useCatalogEditAllowed } from '../hooks/useCatalogEditAllowed';
import { useAdminGroupMember } from '../components/devtools/AdminGroupMemberContext';

const useStyles = makeStyles(theme => ({
  contentWrapper: {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    minHeight: 0,
  },
  centerStage: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    width: '100%',
    paddingTop: theme.spacing(2),
    paddingBottom: theme.spacing(4),
    minHeight: 'min(72vh, 720px)',
  },
  successCheck: {
    fontSize: 72,
    color: theme.palette.success.main,
    marginBottom: theme.spacing(2),
  },
  buttonBar: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'flex-end',
  },
  cancelButton: {
    marginRight: theme.spacing(1),
  },
  retryButton: {
    marginRight: theme.spacing(1),
  },
}));

/**
 * Scaffolder passes `{ TemplateOutputsComponent? }`; typings may also allow `children`.
 */
export type GatedScaffolderTaskPageProps = {
  children?: ReactNode;
  TemplateOutputsComponent?: ComponentType<{ output?: ScaffolderTaskOutput }>;
};

function extractRequestId(output: ScaffolderTaskOutput | undefined): number | undefined {
  if (!output || typeof output !== 'object') return undefined;
  const v = (output as Record<string, unknown>).request_id;
  if (typeof v === 'number' && Number.isFinite(v)) return v;
  if (typeof v === 'string' && /^\d+$/.test(v)) return parseInt(v, 10);
  return undefined;
}

function extractSharingUrl(output: ScaffolderTaskOutput | undefined): string | undefined {
  if (!output || typeof output !== 'object') return undefined;
  const raw = output as Record<string, unknown>;
  const direct = raw.sharingUrl;
  if (typeof direct === 'string' && direct.trim()) {
    return direct.trim();
  }

  const links = raw.links;
  if (Array.isArray(links)) {
    for (const link of links) {
      if (link && typeof link === 'object') {
        const url = (link as Record<string, unknown>).url;
        if (typeof url === 'string' && url.trim()) {
          return url.trim();
        }
      }
    }
  }

  return undefined;
}

function extractRecipientEmails(output: ScaffolderTaskOutput | undefined): string[] {
  if (!output || typeof output !== 'object') return [];
  const raw = output as Record<string, unknown>;
  const list = raw.recipientEmails;
  if (Array.isArray(list)) {
    return list
      .filter((entry): entry is string => typeof entry === 'string' && entry.trim().length > 0)
      .map(entry => entry.trim());
  }
  const legacy = raw.recipientEmail;
  if (typeof legacy === 'string' && legacy.trim()) {
    return legacy
      .split(/[,;\n]+/)
      .map(entry => entry.trim())
      .filter(Boolean);
  }
  return [];
}

function extractRecipientEmailsFromParameters(parameters: unknown): string[] {
  if (!parameters || typeof parameters !== 'object') return [];
  const raw = parameters as Record<string, unknown>;
  const list = raw.recipientEmails;
  if (Array.isArray(list)) {
    return list
      .filter((entry): entry is string => typeof entry === 'string' && entry.trim().length > 0)
      .map(entry => entry.trim());
  }
  const legacy = raw.recipientEmail;
  if (typeof legacy === 'string' && legacy.trim()) {
    return legacy
      .split(/[,;\n]+/)
      .map(entry => entry.trim())
      .filter(Boolean);
  }
  return [];
}

function extractTtlLabel(parameters: unknown): string | undefined {
  if (!parameters || typeof parameters !== 'object') return undefined;
  const ttl = (parameters as Record<string, unknown>).ttl;
  return typeof ttl === 'string' && ttl.trim() ? ttl.trim() : undefined;
}

function isOneTimeSecretTemplate(entityRef: string): boolean {
  return entityRef.trim().toLowerCase() === 'template:default/one-time-secret-sharing';
}

const BACKSTAGE_USER_EMAIL_HEADER = 'X-Backstage-User-Email';

function NotYourTicketMessage() {
  return (
    <Page themeId="website">
      <Header title="Request access" subtitle="This run is linked to another user’s ticket" />
      <Content>
        <Box display="flex" justifyContent="center" paddingTop={4}>
          <Paper style={{ padding: 32, maxWidth: 520, width: '100%' }}>
            <Typography variant="h6" component="h2" gutterBottom style={{ fontWeight: 600 }}>
              This request does not belong to you
            </Typography>
            <Typography variant="body1" color="textSecondary" paragraph>
              The link you opened is tied to someone else’s service request. You cannot view their template
              run. If you need the status of your own requests, open the Requests page.
            </Typography>
            <Button component={Link} to="/requests" variant="outlined" color="primary">
              Go to Requests
            </Button>
          </Paper>
        </Box>
      </Content>
    </Page>
  );
}

function useTaskTicketOwnership(taskId: string | undefined, enabled: boolean) {
  const fetchApi = useApi(fetchApiRef);
  const config = useApi(configApiRef);
  const identityApi = useApi(identityApiRef);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<{
    found: boolean;
    belongs_to_caller: boolean;
  } | null>(null);

  useEffect(() => {
    if (!enabled || !taskId?.trim()) {
      setLoading(false);
      setResult(null);
      return undefined;
    }
    let cancelled = false;
    setLoading(true);
    setResult(null);
    (async () => {
      try {
        const baseUrl = config.getString('backend.baseUrl');
        const url = `${baseUrl}/api/requests/requests/task-access/${encodeURIComponent(taskId.trim())}`;
        const headers: Record<string, string> = {};
        try {
          const profile = await identityApi.getProfileInfo();
          const email = profile.email?.trim();
          if (email && email.includes('@')) headers[BACKSTAGE_USER_EMAIL_HEADER] = email;
        } catch {
          /* ignore */
        }
        const res = await fetchApi.fetch(url, {
          credentials: 'include',
          headers: Object.keys(headers).length > 0 ? headers : undefined,
        });
        const body = await res.json().catch(() => ({}));
        if (cancelled) return;
        if (!res.ok) {
          setResult({ found: false, belongs_to_caller: false });
          return;
        }
        setResult({
          found: Boolean(body.found),
          belongs_to_caller: Boolean(body.belongs_to_caller),
        });
      } catch {
        if (!cancelled) setResult({ found: false, belongs_to_caller: false });
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [enabled, taskId, fetchApi, config, identityApi]);

  return { loading, result };
}

function RequestSubmittedSummary({
  requestId,
  sharingUrl,
  recipientEmails,
  ttlLabel,
  isOneTimeSecret,
}: {
  requestId: number | undefined;
  sharingUrl?: string;
  recipientEmails?: string[];
  ttlLabel?: string;
  isOneTimeSecret?: boolean;
}) {
  const classes = useStyles();
  const [linkCopied, setLinkCopied] = useState(false);
  const handleCopySharingLink = useCallback(async () => {
    if (!sharingUrl) return;
    try {
      await navigator.clipboard.writeText(sharingUrl);
      setLinkCopied(true);
      window.setTimeout(() => setLinkCopied(false), 2000);
    } catch {
      /* clipboard may be unavailable in some contexts */
    }
  }, [sharingUrl]);
  const successTitle = isOneTimeSecret ? 'One time secret created' : 'Request submitted';
  return (
    <Paper
      elevation={2}
      style={{
        padding: 32,
        maxWidth: 520,
        width: '100%',
        textAlign: 'center',
      }}
    >
      <CheckCircle className={classes.successCheck} aria-hidden />
      <Typography variant="h5" component="h2" gutterBottom style={{ fontWeight: 600 }}>
        {successTitle}
      </Typography>
      <Typography
        variant="body1"
        color="textSecondary"
        paragraph
        style={{
          marginBottom:
            requestId !== undefined && requestId !== null ? 16 : 0,
        }}
      >
        {isOneTimeSecret ? (
          <>
            Please share the below link with intended recipient/s
            {recipientEmails && recipientEmails.length > 0 ? (
              <>
                {' '}
                <Box component="span" fontWeight={600}>
                  &quot;{recipientEmails.join(', ')}&quot;
                </Box>
              </>
            ) : null}{' '}
            to access the secret.
          </>
        ) : (
          'Your request has been recorded. You can track it anytime from the Requests page.'
        )}
      </Typography>
      {requestId !== undefined && requestId !== null ? (
        <Typography variant="body1">
          Request number:{' '}
          <Link
            to={`/requests/${requestId}`}
            target="_blank"
            rel="noopener noreferrer"
            style={{ fontWeight: 600 }}
          >
            {requestId}
          </Link>
        </Typography>
      ) : null}
      {sharingUrl ? (
        <Box marginTop={2} style={{ textAlign: 'left' }}>
          <Typography variant="body1" gutterBottom style={{ textAlign: 'center' }}>
            One-time secret access link:
          </Typography>
          <Box
            display="flex"
            alignItems="flex-start"
            justifyContent="center"
            flexWrap="nowrap"
            style={{ marginTop: 12, gap: 4, width: '100%' }}
          >
            <Typography
              variant="body2"
              color="textSecondary"
              style={{ wordBreak: 'break-all', flex: '1 1 auto', minWidth: 0, textAlign: 'left' }}
            >
              {sharingUrl}
            </Typography>
            <Tooltip title={linkCopied ? 'Copied' : 'Copy sharing link to clipboard'}>
              <IconButton
                size="small"
                onClick={handleCopySharingLink}
                aria-label="Copy sharing link to clipboard"
                style={{ flexShrink: 0, marginTop: -4 }}
              >
                <FileCopy fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
          <Typography
            variant="body2"
            color="textSecondary"
            style={{ marginTop: 16, fontWeight: 600 }}
          >
            This link needs to be shared to the recipient in the TTL time
            {ttlLabel ? ` (${ttlLabel})` : ''} or it will be expired after the TTL time
            {ttlLabel ? ` (${ttlLabel})` : ''}.
          </Typography>
        </Box>
      ) : null}
    </Paper>
  );
}

/**
 * Same controls as the default task page but no log stream or log toggle.
 */
function RestrictedTaskPageContent({
  TemplateOutputsComponent: _TemplateOutputsComponent,
}: GatedScaffolderTaskPageProps) {
  const { taskId: taskIdParam } = useParams();
  const taskId = taskIdParam ?? '';
  const templateRouteRef = useRouteRef(scaffolderPlugin.routes.selectedTemplate);
  const navigate = useNavigate();
  const analytics = useAnalytics();
  const scaffolderApi = useApi(scaffolderApiRef);
  const entityPresentationApi = useApi(entityPresentationApiRef);
  const taskStream = useTaskEventStream(taskId);
  const classes = useStyles();
  const { t } = useTranslationRef(scaffolderTranslationRef);
  const [buttonBarVisible, setButtonBarVisibleState] = useState(true);

  const { allowed: canCancelTask } = usePermission({
    permission: taskCancelPermission,
    resourceRef: taskId,
  });
  const { allowed: canReadTask } = usePermission({
    permission: taskReadPermission,
    resourceRef: taskId,
  });
  const { allowed: canCreateTask } = usePermission({
    permission: taskCreatePermission,
  });
  const canStartOver = canReadTask && canCreateTask;

  useEffect(() => {
    if (taskStream.completed && !taskStream.error) {
      setButtonBarVisibleState(false);
    }
  }, [taskStream.error, taskStream.completed]);

  const { value: presentation } = useAsync(async () => {
    const templateEntityRef = taskStream.task?.spec.templateInfo?.entityRef;
    if (!templateEntityRef) return undefined;
    return entityPresentationApi.forEntity(templateEntityRef).promise;
  }, [entityPresentationApi, taskStream.task?.spec.templateInfo?.entityRef]);

  const isRetryableTask =
    taskStream.task?.spec.EXPERIMENTAL_recovery?.EXPERIMENTAL_strategy === 'startOver';
  const canRetry = canReadTask && canCreateTask && isRetryableTask;

  const startOver = useCallback(() => {
    const { namespace, name } = taskStream.task?.spec.templateInfo?.entity?.metadata ?? {};
    const formData = taskStream.task?.spec.parameters ?? {};
    if (!namespace || !name) {
      return;
    }
    analytics.captureEvent('click', 'Task has been started over');
    navigate({
      pathname: templateRouteRef({
        namespace,
        templateName: name,
      }),
      search: `?${qs.stringify({ formData: JSON.stringify(formData) })}`,
    });
  }, [
    analytics,
    navigate,
    taskStream.task?.spec.parameters,
    taskStream.task?.spec.templateInfo?.entity?.metadata,
    templateRouteRef,
  ]);

  const [, { execute: triggerRetry }] = useAsync$1(async () => {
    if (taskId) {
      analytics.captureEvent('retried', 'Template has been retried');
      await scaffolderApi.retry?.(taskId);
    }
  });

  const [{ status: cancelStatus }, { execute: triggerCancel }] = useAsync$1(async () => {
    if (taskId) {
      analytics.captureEvent('cancelled', 'Template has been cancelled');
      await scaffolderApi.cancelTask(taskId);
    }
  });

  const cancelEnabled = !(taskStream.cancelled || taskStream.completed);

  /** Finished successfully (not cancelled) — same “Request submitted” card for live run or pasted /create/tasks URL. */
  const successMinimal =
    taskStream.completed && !taskStream.error && !taskStream.cancelled;
  const requestId = extractRequestId(taskStream.output);
  const isOneTimeSecret = isOneTimeSecretTemplate(
    taskStream.task?.spec.templateInfo?.entityRef ?? '',
  );
  const sharingUrl = isOneTimeSecret ? extractSharingUrl(taskStream.output) : undefined;
  const recipientEmails = isOneTimeSecret
    ? (() => {
        const fromOutput = extractRecipientEmails(taskStream.output);
        if (fromOutput.length > 0) return fromOutput;
        return extractRecipientEmailsFromParameters(taskStream.task?.spec.parameters);
      })()
    : undefined;
  const ttlLabel = isOneTimeSecret
    ? extractTtlLabel(taskStream.task?.spec.parameters)
    : undefined;

  /** No step names / backend jargon — spinner only until success, error, or cancel. */
  const friendlySubmitting =
    !successMinimal && !taskStream.error && !taskStream.cancelled && !taskStream.completed;

  const cancelledOnly = taskStream.cancelled && !taskStream.error;

  const defaultPageTitle = presentation
    ? t('ongoingTask.pageTitle.hasTemplateName', {
        templateName: presentation.primaryTitle,
      })
    : t('ongoingTask.pageTitle.noTemplateName');

  const successPageTitle = isOneTimeSecret ? 'One time secret created' : 'Request submitted';

  let pageTitleOverride: string;
  if (successMinimal) {
    pageTitleOverride = successPageTitle;
  } else if (friendlySubmitting) {
    pageTitleOverride = 'Submitting request';
  } else {
    pageTitleOverride = defaultPageTitle;
  }

  let headerTitleNode: ReactNode;
  if (successMinimal) {
    headerTitleNode = (
      <Typography variant="h6" component="span">
        {successPageTitle}
      </Typography>
    );
  } else if (friendlySubmitting) {
    headerTitleNode = (
      <Typography variant="h6" component="span">
        Submitting your request…
      </Typography>
    );
  } else {
    headerTitleNode = (
      <div>
        {t('ongoingTask.title')}{' '}
        <code>{presentation ? presentation.primaryTitle : ''}</code>
      </div>
    );
  }

  const buttonBar = buttonBarVisible ? (
    <Box paddingBottom={2}>
      <Paper>
        <Box padding={2}>
          <div className={classes.buttonBar}>
            <Button
              className={classes.cancelButton}
              disabled={
                !cancelEnabled ||
                (cancelStatus !== 'not-executed' && !isRetryableTask) ||
                !canCancelTask
              }
              onClick={triggerCancel}
              data-testid="cancel-button"
            >
              {t('ongoingTask.cancelButtonTitle')}
            </Button>
            {isRetryableTask && (
              <Button
                className={classes.retryButton}
                disabled={cancelEnabled || !canRetry}
                onClick={triggerRetry}
                data-testid="retry-button"
              >
                {t('ongoingTask.retryButtonTitle')}
              </Button>
            )}
            <Button
              variant="contained"
              color="primary"
              disabled={cancelEnabled || !canStartOver}
              onClick={startOver}
              data-testid="start-over-button"
            >
              {t('ongoingTask.startOverButtonTitle')}
            </Button>
          </div>
        </Box>
      </Paper>
    </Box>
  ) : null;

  let mainContent: ReactNode;
  if (successMinimal) {
    mainContent = (
      <Box className={classes.centerStage}>
        <RequestSubmittedSummary
          requestId={requestId}
          sharingUrl={sharingUrl}
          recipientEmails={recipientEmails}
          ttlLabel={ttlLabel}
          isOneTimeSecret={isOneTimeSecret}
        />
      </Box>
    );
  } else if (cancelledOnly) {
    mainContent = (
      <>
        <Paper style={{ padding: 24, maxWidth: 560 }}>
          <Typography variant="body1">This run was stopped before it finished.</Typography>
        </Paper>
        {buttonBar}
      </>
    );
  } else if (taskStream.error) {
    mainContent = (
      <>
        <Box paddingBottom={2}>
          <ErrorPanel
            error={taskStream.error}
            titleFormat="markdown"
            title={taskStream.error.message}
          />
        </Box>
        {buttonBar}
      </>
    );
  } else {
    mainContent = (
      <>
        <Box display="flex" justifyContent="center" paddingY={4}>
          <Paper
            style={{
              padding: 48,
              textAlign: 'center',
              maxWidth: 440,
            }}
          >
            <CircularProgress />
            <Typography variant="h6" style={{ marginTop: 24 }}>
              Submitting your request…
            </Typography>
            <Typography variant="body2" color="textSecondary" style={{ marginTop: 12 }}>
              Please wait; this usually takes a few moments. You can cancel if you need to stop.
            </Typography>
          </Paper>
        </Box>
        {buttonBar}
      </>
    );
  }

  return (
    <>
      <Header
        pageTitleOverride={pageTitleOverride}
        title={headerTitleNode}
        subtitle={undefined}
      />
      <Content className={classes.contentWrapper}>{mainContent}</Content>
    </>
  );
}

function GatedScaffolderTaskPageImpl(props: GatedScaffolderTaskPageProps) {
  const { taskId: taskIdParam } = useParams();
  const taskId = taskIdParam ?? '';
  const taskStream = useTaskEventStream(taskId);
  const adminMember = useAdminGroupMember();
  const templateEntityRef = taskStream.task?.spec.templateInfo?.entityRef ?? '';
  const editAllowed = useCatalogEditAllowed(templateEntityRef);

  const taskMetaReady = Boolean(taskStream.task || taskStream.error);

  const runOwnershipCheck = adminMember === false && taskMetaReady;

  const { loading: ownershipLoading, result: ownership } = useTaskTicketOwnership(
    taskIdParam,
    runOwnershipCheck,
  );

  const waitingForEditCheck =
    adminMember === false && Boolean(templateEntityRef) && editAllowed === null;

  if (adminMember === null) {
    return (
      <Page themeId="website">
        <Header title="Template run" />
        <Content>
          <Box display="flex" justifyContent="center" alignItems="center" minHeight={240}>
            <Progress />
          </Box>
        </Content>
      </Page>
    );
  }

  if (adminMember === true) {
    return <DefaultTaskPage {...props} />;
  }

  if (!taskMetaReady) {
    return (
      <Page themeId="website">
        <Header title="Template run" />
        <Content>
          <Box display="flex" justifyContent="center" alignItems="center" minHeight={240}>
            <Progress />
          </Box>
        </Content>
      </Page>
    );
  }

  if (runOwnershipCheck && ownershipLoading) {
    return (
      <Page themeId="website">
        <Header title="Template run" />
        <Content>
          <Box display="flex" justifyContent="center" alignItems="center" minHeight={240}>
            <Progress />
          </Box>
        </Content>
      </Page>
    );
  }

  if (waitingForEditCheck) {
    return (
      <Page themeId="website">
        <Header title="Template run" />
        <Content>
          <Box display="flex" justifyContent="center" alignItems="center" minHeight={240}>
            <Progress />
          </Box>
        </Content>
      </Page>
    );
  }

  /** Developer+ on template: full logs only for their ticket (or before Firestore links the task). */
  if (adminMember === false && editAllowed === true) {
    if (ownership?.found === true && ownership.belongs_to_caller === false) {
      return <NotYourTicketMessage />;
    }
    return <DefaultTaskPage {...props} />;
  }

  if (ownership?.found === true && ownership.belongs_to_caller === false) {
    return <NotYourTicketMessage />;
  }

  const { namespace, name } = taskStream.task?.spec.templateInfo?.entity?.metadata ?? {};
  return (
    <AnalyticsContext
      attributes={{
        entityRef:
          name && namespace
            ? stringifyEntityRef({ kind: 'template', namespace, name })
            : undefined,
        taskId,
      }}
    >
      <Page themeId="website">
        <RestrictedTaskPageContent {...props} />
      </Page>
    </AnalyticsContext>
  );
}

/** Router types only declare `children`; runtime also passes `TemplateOutputsComponent`. */
export const GatedScaffolderTaskPage = GatedScaffolderTaskPageImpl as ComponentType<{
  children?: ReactNode;
}>;
